import type { Camera, Scene, SceneControl, WebGLRenderer } from '@anov/3d-core'
import { use } from '@anov/3d-core'
import { BlendFunction, DepthDownsamplingPass, EdgeDetectionMode, EffectComposer, EffectPass, NormalPass, PredicationMode, RenderPass, SMAAEffect, SMAAPreset, SSAOEffect, TextureEffect } from 'postprocessing'

const { useComposerFrame } = use

export const initPostEffectsV2 = (
  scene: Scene,
  renderer: WebGLRenderer,
  camera: Camera,
  sceneControl: SceneControl,
) => {
  const composer = new EffectComposer(renderer)
  composer.addPass(new RenderPass(scene, camera))

  const normalPass = new NormalPass(scene, camera)
  const depthDownsamplingPass = new DepthDownsamplingPass({
    normalBuffer: normalPass.texture,
    resolutionScale: 0.5,
  })

  const normalDepthBuffer = depthDownsamplingPass.texture

  const smaaEffect = new SMAAEffect({
    preset: SMAAPreset.MEDIUM,
    edgeDetectionMode: EdgeDetectionMode.COLOR,
    predicationMode: PredicationMode.DEPTH,
  })

  smaaEffect.edgeDetectionMaterial.setEdgeDetectionThreshold(0.01)

  const ssaoEffect = new SSAOEffect(camera, normalPass.texture, {
    blendFunction: BlendFunction.MULTIPLY,
    distanceScaling: true,
    depthAwareUpsampling: true,
    normalDepthBuffer,
    samples: 9,
    rings: 7,
    distanceThreshold: 0.02, // Render up to a distance of ~20 world units
    distanceFalloff: 0.0025, // with an additional ~2.5 units of falloff.
    rangeThreshold: 0.0003, // Occlusion proximity of ~0.3 world units
    rangeFalloff: 0.0001, // with ~0.1 units of falloff.
    luminanceInfluence: 0.7,
    minRadiusScale: 0.33,
    radius: 0.1,
    intensity: 1.33,
    bias: 0.025,
    fade: 0.01,
    color: null,
    resolutionScale: 0.5,
  })

  const textureEffect = new TextureEffect({
    blendFunction: BlendFunction.SKIP,
    texture: depthDownsamplingPass.texture,
  })

  const effectPass = new EffectPass(camera, smaaEffect, ssaoEffect, textureEffect)

  composer.addPass(normalPass)
  composer.addPass(depthDownsamplingPass)
  composer.addPass(effectPass)

  useComposerFrame(sceneControl, () => {
    composer.render()
  })
}